import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Stack,
  Card,
} from '@mui/material';
import {
  ShieldOutlined as ShieldIcon,
  FolderSpecialOutlined as DataIcon,
  PsychologyOutlined as PurposeIcon,
  LockOutlined as LockIcon,
  CookieOutlined as CookieIcon,
  HubOutlined as IntegrationIcon,
  GavelOutlined as RightsIcon,
  EmailOutlined as EmailIcon,
  LocationOnOutlined as LocationIcon,
  LanguageOutlined as WebIcon,
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';

const sections = [
  { id: 'information-we-collect', title: '1. Information We Collect', icon: <DataIcon sx={{ color: '#C59B27' }} /> },
  { id: 'how-we-use-information', title: '2. How We Use Your Information', icon: <PurposeIcon sx={{ color: '#C59B27' }} /> },
  { id: 'data-security', title: '3. Data Security & Confidentiality', icon: <LockIcon sx={{ color: '#C59B27' }} /> },
  { id: 'cookies-policy', title: '4. Cookies & Tracking Technologies', icon: <CookieIcon sx={{ color: '#C59B27' }} /> },
  { id: 'third-party-services', title: '5. Third-Party Services & Integrations', icon: <IntegrationIcon sx={{ color: '#C59B27' }} /> },
  { id: 'your-rights', title: '6. Your Legal Rights & Choices', icon: <RightsIcon sx={{ color: '#C59B27' }} /> },
  { id: 'contact-us', title: '7. Contact Information & Grievance', icon: <EmailIcon sx={{ color: '#C59B27' }} /> },
];

const PrivacyPolicy: React.FC = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <SEOHead
        title="Privacy Policy | Fingard Partners"
        description="Read Fingard Partners privacy policy to understand how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, CA firm privacy, Fingard Partners, confidentiality, financial data security"
      />

      {/* Hero Header Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0B2847 0%, #123D6B 60%, #081E35 100%)',
          color: '#FFFFFF',
          pt: { xs: 8, md: 10 },
          pb: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          fontFamily: '"Poppins", sans-serif',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 850, mx: 'auto', textAlign: 'center' }}>
            <Chip
              icon={<ShieldIcon sx={{ '&&': { color: '#C59B27', fontSize: 18 } }} />}
              label="Legal & Data Governance"
              sx={{
                backgroundColor: 'rgba(197, 155, 39, 0.15)',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '0.85rem',
                letterSpacing: '0.04em',
                px: 1.5,
                py: 0.5,
                mb: 2.5,
                borderRadius: 3,
                border: '1px solid rgba(197, 155, 39, 0.4)',
                fontFamily: '"Poppins", sans-serif',
              }}
            />

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.25rem', sm: '3rem', md: '3.75rem' },
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 2.5,
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              Privacy Policy
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: '#E2E8F0',
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.15rem' },
                lineHeight: 1.7,
                maxWidth: 720,
                mx: 'auto',
                mb: 3,
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              At Fingard Partners LLP, your financial privacy and data confidentiality are of paramount importance. This policy explains how we collect, handle, protect, and safeguard your data across our advisory platforms.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Chip
                label="Entity: Fingard Partners LLP"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontFamily: '"Poppins", sans-serif',
                }}
              />
              <Chip
                label="Website: fingard.co"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontFamily: '"Poppins", sans-serif',
                }}
              />
              <Chip
                label="Last Updated: August 2026"
                sx={{
                  backgroundColor: 'rgba(197, 155, 39, 0.2)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(197, 155, 39, 0.4)',
                  fontFamily: '"Poppins", sans-serif',
                }}
              />
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ py: { xs: 6, md: 9 }, backgroundColor: '#F8FAFC' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Left Quick-Navigation Sidebar */}
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box sx={{ position: 'sticky', top: 100 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 4px 20px rgba(11, 40, 71, 0.04)',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontSize: '0.8rem',
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Table of Contents
                  </Typography>
                  <List disablePadding>
                    {sections.map((sec) => (
                      <ListItem
                        key={sec.id}
                        button
                        onClick={() => scrollToSection(sec.id)}
                        sx={{
                          py: 1.2,
                          px: 1.5,
                          borderRadius: 2,
                          mb: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(18, 61, 107, 0.06)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>{sec.icon}</ListItemIcon>
                        <ListItemText
                          primary={sec.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: '#102A43',
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Divider sx={{ my: 2.5 }} />

                  <Box sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(18, 61, 107, 0.04)' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        color: '#0B2847',
                        mb: 0.5,
                        fontFamily: '"Poppins", sans-serif',
                      }}
                    >
                      Need Privacy Clarifications?
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#486581',
                        mb: 1.5,
                        fontFamily: '"Poppins", sans-serif',
                      }}
                    >
                      Contact our dedicated Data Protection Officer for any specific questions.
                    </Typography>
                    <Button
                      component={RouterLink}
                      to="/contact"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderColor: '#123D6B',
                        color: '#123D6B',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        fontFamily: '"Poppins", sans-serif',
                        '&:hover': {
                          borderColor: '#0B2847',
                          backgroundColor: 'rgba(18, 61, 107, 0.06)',
                        },
                      }}
                    >
                      Contact Compliance Desk
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Grid>

            {/* Right Main Policy Content */}
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 5, md: 6 },
                  borderRadius: 3,
                  border: '1px solid #E2E8F0',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 4px 20px rgba(11, 40, 71, 0.04)',
                }}
              >
                {/* Introduction */}
                <Box sx={{ mb: 5 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Introduction & Scope
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Welcome to <strong>Fingard Partners LLP</strong> (&quot;Fingard&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). We operate the website located at <strong>fingard.co</strong> along with associated digital portals, tax calculation tools, and client accounting document vaults. As a professional Chartered Accountancy and financial advisory practice, we are governed by strict confidentiality guidelines, Indian statutory data protection laws, and the professional code of conduct mandated by the Institute of Chartered Accountants of India (ICAI).
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    This Privacy Policy articulates our transparent practices regarding the collection, processing, storage, transmission, and protection of your Personally Identifiable Information (PII) and confidential financial records.
                  </Typography>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Section 1 */}
                <Box id="information-we-collect" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <DataIcon sx={{ color: '#C59B27' }} /> 1. Information We Collect
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    To deliver accurate tax computation, statutory compliance, corporate secretarial, and financial advisory services, we collect necessary data categories:
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E2E8F0', p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0B2847', mb: 1, fontFamily: '"Poppins", sans-serif' }}>
                          A. Personal & Identification Information
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, fontFamily: '"Poppins", sans-serif' }}>
                          Full name, official email address, mobile number, postal address, Permanent Account Number (PAN), Aadhaar details (where voluntarily provided for e-verification/KYC), Director Identification Number (DIN), and passport details for NRI tax assessments.
                        </Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E2E8F0', p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0B2847', mb: 1, fontFamily: '"Poppins", sans-serif' }}>
                          B. Financial, Tax & Corporate Records
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, fontFamily: '"Poppins", sans-serif' }}>
                          Form 16, Form 26AS, Annual Information Statement (AIS/TIS), bank statements, salary slips, investment receipts, capital gains statements, profit and loss statements, balance sheets, GST invoices, and incorporation documents.
                        </Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E2E8F0', p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0B2847', mb: 1, fontFamily: '"Poppins", sans-serif' }}>
                          C. Technical & Diagnostic Data
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, fontFamily: '"Poppins", sans-serif' }}>
                          Internet Protocol (IP) addresses, browser type, operating system version, access timestamps, referral sources, and analytical metrics regarding interaction with our calculation utilities.
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Section 2 */}
                <Box id="how-we-use-information" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <PurposeIcon sx={{ color: '#C59B27' }} /> 2. How We Use Your Information
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    We collect and process your information exclusively for legitimate professional, statutory, and contractual purposes:
                  </Typography>

                  <List disablePadding>
                    {[
                      'Filing Income Tax Returns (ITR-1 through ITR-7) and representation before tax authorities.',
                      'Preparing and filing monthly/quarterly GST returns (GSTR-1, GSTR-3B, GSTR-9) and reconciling Input Tax Credit.',
                      'Executing Ministry of Corporate Affairs (MCA/ROC) statutory compliance, company incorporations, and annual filings.',
                      'Providing tailored tax planning strategies, payroll processing, and outsourced CFO advisory.',
                      'Sending critical filing acknowledgements, tax demand notices, challans, and statutory deadline alerts.',
                      'Processing service retainers, advisory invoices, and secure payment transactions.',
                      'Complying with regulatory obligations under Indian tax laws and ICAI professional guidelines.',
                    ].map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.8 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckIcon sx={{ color: '#C59B27', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{
                            fontSize: '0.92rem',
                            color: '#486581',
                            lineHeight: 1.6,
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Section 3 */}
                <Box id="data-security" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <LockIcon sx={{ color: '#C59B27' }} /> 3. Data Security & Confidentiality
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    As a Chartered Accountancy firm handling confidential financial records, we employ multi-layered institutional safeguards:
                  </Typography>

                  <Box sx={{ p: 3, backgroundColor: 'rgba(18, 61, 107, 0.04)', borderRadius: 2.5, mb: 2.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0B2847', mb: 1, fontFamily: '"Poppins", sans-serif' }}>
                      Key Security Measures:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, mb: 1.5, fontFamily: '"Poppins", sans-serif' }}>
                      • <strong>End-to-End Encryption:</strong> All data transmissions are secured using 256-bit TLS/SSL encryption. Documents uploaded to our Document Vault are encrypted at rest using industry-standard AES-256 protocols.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, mb: 1.5, fontFamily: '"Poppins", sans-serif' }}>
                      • <strong>Strict Role-Based Access:</strong> Access to sensitive client records is strictly restricted to assigned Chartered Accountants, legal associates, and verified tax professionals on a need-to-know basis.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, mb: 1.5, fontFamily: '"Poppins", sans-serif' }}>
                      • <strong>Professional Non-Disclosure:</strong> All partners, staff members, and technical contractors are bound by strict non-disclosure agreements (NDAs) and the Chartered Accountants Act, 1949.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.7, fontFamily: '"Poppins", sans-serif' }}>
                      • <strong>Zero Third-Party Data Monetization:</strong> We do NOT sell, rent, lease, or commercialize your personal or financial data to any third-party marketing firms under any circumstances.
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Section 4 */}
                <Box id="cookies-policy" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <CookieIcon sx={{ color: '#C59B27' }} /> 4. Cookies & Tracking Technologies
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Our website (<strong>fingard.co</strong>) uses cookies and similar tracking technologies to enhance user session persistence, provide seamless interactive tools (such as ITR calculators and diagnostic wizards), and analyze aggregated website traffic.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.7,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    You can instruct your browser to refuse cookies or alert you when cookies are being sent. However, disabling essential cookies may impact certain interactive calculation features or client portal access.
                  </Typography>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Section 5 */}
                <Box id="third-party-services" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <IntegrationIcon sx={{ color: '#C59B27' }} /> 5. Third-Party Services & Integrations
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    In executing our statutory mandate, we may interface with verified external gateways:
                  </Typography>
                  <List disablePadding>
                    <ListItem sx={{ px: 0, py: 0.8 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon sx={{ color: '#C59B27', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Government Statutory Portals: e-Filing Income Tax Portal (ITD), GST Common Portal (GSTN), MCA21 Portal, and TRACES for official filings on your explicit instruction."
                        primaryTypographyProps={{ fontSize: '0.9rem', color: '#486581', lineHeight: 1.6, fontFamily: '"Poppins", sans-serif' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.8 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon sx={{ color: '#C59B27', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Payment Gateways: PCI-DSS compliant processing platforms (such as Stripe / Razorpay). Fingard Partners LLP does not store complete credit card or debit card credentials."
                        primaryTypographyProps={{ fontSize: '0.9rem', color: '#486581', lineHeight: 1.6, fontFamily: '"Poppins", sans-serif' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.8 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon sx={{ color: '#C59B27', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Secure Cloud Infrastructure: Enterprise-grade cloud server hosting adhering to ISO 27001 and SOC-2 data center standards."
                        primaryTypographyProps={{ fontSize: '0.9rem', color: '#486581', lineHeight: 1.6, fontFamily: '"Poppins", sans-serif' }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Section 6 */}
                <Box id="your-rights" sx={{ mb: 5, scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <RightsIcon sx={{ color: '#C59B27' }} /> 6. Your Legal Rights & Choices
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 2,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    Under applicable data protection laws (including the Digital Personal Data Protection Act and Information Technology Act, 2000), you maintain the following rights:
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { title: 'Right to Access', desc: 'Request copies of the personal and tax documentation stored within our active engagement records.' },
                      { title: 'Right to Rectification', desc: 'Request corrections of any inaccurate, outdated, or incomplete financial details.' },
                      { title: 'Right to Data Portability', desc: 'Obtain your structured financial filings, computation statements, and trial balances.' },
                      { title: 'Right to Erasure', desc: 'Request removal of personal data, subject to statutory retention periods required by the Income Tax Act & Companies Act.' },
                    ].map((right, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, borderColor: '#E2E8F0', p: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0B2847', mb: 0.5, fontFamily: '"Poppins", sans-serif' }}>
                            {right.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#486581', fontSize: '0.85rem', lineHeight: 1.5, fontFamily: '"Poppins", sans-serif' }}>
                            {right.desc}
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Section 7 */}
                <Box id="contact-us" sx={{ scrollMarginTop: '110px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0B2847',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <EmailIcon sx={{ color: '#C59B27' }} /> 7. Contact Us & Grievance Redressal
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#486581',
                      lineHeight: 1.8,
                      mb: 3,
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    For any questions, clarifications, privacy requests, or grievances concerning this Privacy Policy, please contact our Data Governance Officer at:
                  </Typography>

                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid #E2E8F0',
                      backgroundColor: '#F8FAFC',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0B2847', mb: 1.5, fontFamily: '"Poppins", sans-serif' }}>
                      Fingard Partners LLP
                    </Typography>
                    <Stack spacing={1.2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <WebIcon sx={{ color: '#123D6B', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#102A43', fontWeight: 500, fontFamily: '"Poppins", sans-serif' }}>
                          Website: <a href="https://fingard.co" target="_blank" rel="noopener noreferrer" style={{ color: '#123D6B', textDecoration: 'underline' }}>fingard.co</a>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EmailIcon sx={{ color: '#123D6B', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#102A43', fontWeight: 500, fontFamily: '"Poppins", sans-serif' }}>
                          Privacy Email: <a href="mailto:coo.fingard@gmail.com" style={{ color: '#123D6B', textDecoration: 'none' }}>coo.fingard@gmail.com</a>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocationIcon sx={{ color: '#123D6B', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#102A43', fontWeight: 500, fontFamily: '"Poppins", sans-serif' }}>
                          Office: Fingard Partners LLP, Chartered Accountants & Corporate Advisors, India
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #E2E8F0' }}>
                      <Button
                        component={RouterLink}
                        to="/contact"
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          backgroundColor: '#123D6B',
                          color: '#FFFFFF',
                          fontWeight: 600,
                          fontFamily: '"Poppins", sans-serif',
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: '#0B2847',
                          },
                        }}
                      >
                        Submit Privacy Inquiry
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicy;
